import { openBooking } from "@/lib/booking";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useLang, FAQS } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Header";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CalendarClock, Trophy, GraduationCap, Users, IdCard, CreditCard, ArrowRight, MapPin, ExternalLink, Smartphone, Zap, Star,
} from "lucide-react";

const HERO = "/court-real.jpg";
const COMMUNITY = "https://images.unsplash.com/photo-1612534847738-b3af9bc31f0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const COURT_BANNER = "/court-taf.jpg";
const COURT_TILES = [
  { src: "https://images.unsplash.com/photo-1770230901556-4e1c0bacfb09?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Padel players in action on a blue court in Kos" },
  { src: "https://images.unsplash.com/photo-1763560836998-bdeb531e8541?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Evening padel match under floodlights" },
  { src: "https://images.unsplash.com/photo-1767128890576-ecc5c643f9c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Group of padel players with rackets on court" },
  { src: "https://images.unsplash.com/photo-1699117686612-ece525e4f91a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Padel court net and white lines detail" },
];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// TODO: replace with real player reviews when available (kept out of SEO structured data to stay honest).
const TESTIMONIALS = [
  { quote: "Best padel court on the island — pristine glass court and so easy to book through Playtomic.", name: "Marco", country: "Italy" },
  { quote: "Booked a late evening match under the floodlights. Perfect surface and a great vibe among the palms.", name: "Sofia", country: "Germany" },
  { quote: "Found two players through Playtomic and had a brilliant Americano. Will be back next summer!", name: "Nikos", country: "Greece" },
  { quote: "Smooth booking, friendly reception at TAF Beach Villas, and a top-quality court. Highly recommend.", name: "Emma", country: "UK" },
];
const fileUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function Landing() {
  const { t, lang } = useLang();
  const [gallery, setGallery] = useState([]);
  const [heroImg, setHeroImg] = useState(HERO);

  useEffect(() => {
    api.get("/settings/court").then(({ data }) => {
      if (data.hero_image) setHeroImg(fileUrl(data.hero_image));
      setGallery((data.gallery || []).map(fileUrl));
    }).catch(() => {});
  }, []);

  const features = [
    { icon: Trophy, t: t("f2_t"), d: t("f2_d"), span: "md:col-span-2", to: "/tournaments" },
    { icon: Users, t: t("f4_t"), d: t("f4_d"), to: "/find-players" },
    { icon: CalendarClock, t: t("f5_t"), d: t("f5_d"), to: "/book" },
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Palm decorations */}
      <div className="palm-deco sway hidden md:block"
        style={{ width: 420, height: 420, top: -90, right: -120, "--rot": "18deg", opacity: 0.45, backgroundImage: "url(/palm-frond.png)" }} />
      <div className="palm-deco hidden md:block"
        style={{ width: 300, height: 300, top: 380, left: -110, transform: "scaleX(-1) rotate(-12deg)", opacity: 0.35, backgroundImage: "url(/monstera.png)" }} />

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-5 pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up relative z-10">
            <div className="inline-flex items-center gap-2 label-caps text-primary mb-6 border border-primary/30 rounded-full px-4 py-2 bg-primary/5">
              <MapPin className="w-3.5 h-3.5" /> {t("hero_tag")}
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-foreground mb-6">
              {t("hero_title")}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl mb-9">
              {t("hero_sub")}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={openBooking}
                data-testid="hero-book-btn"
                className="rounded-full bg-primary text-primary-foreground font-bold text-base px-8 py-6 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(47,93,66,0.3)] transition-all"
              >
                {t("hero_cta")} <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-5 text-xs text-muted-foreground" data-testid="playtomic-badge">
              <span>Powered by</span>
              <img src="/playtomic-icon.png" alt="Playtomic" className="w-5 h-5 rounded-[5px]" />
              <span className="font-display font-black tracking-tight text-foreground/80">Playtomic</span>
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-black/5 aspect-[4/5] lg:aspect-square">
              <img src={heroImg} alt="Padel court at sunset" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/25 to-transparent" />
            </div>
            <div className="palm-deco hidden lg:block sway"
              style={{ width: 220, height: 220, bottom: -50, right: -40, "--rot": "10deg", opacity: 0.9, zIndex: 20, backgroundImage: "url(/monstera.png)" }} />
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="relative max-w-7xl mx-auto px-5 py-16 md:py-24 z-10">
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-3 text-foreground">
          {t("feat_title")}
        </h2>
        <div className="h-1 w-16 bg-primary rounded-full mb-12" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
          {features.map((f, i) => (
            <button
              key={f.t}
              onClick={openBooking}
              className="text-left bg-card border rounded-2xl p-4 sm:p-6 hover:border-primary/50 hover:shadow-lg hover:-translate-y-1 transition-all group cursor-pointer"
              data-testid={`feature-card-${i}`}
            >
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-primary/20 transition-colors">
                <f.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <h3 className="font-display font-bold text-base sm:text-lg mb-1 text-foreground flex items-center gap-1.5">
                {f.t}
                <ArrowRight className="w-3.5 h-3.5 text-primary opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </h3>
              <p className="text-muted-foreground leading-snug text-xs sm:text-sm">{f.d}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Gallery (admin-managed) */}
      {gallery.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-5 pb-8 md:pb-16 z-10" data-testid="landing-gallery">
          <h2 className="font-display font-black text-3xl md:text-4xl text-foreground mb-6">{t("gallery_title")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((g, i) => (
              <div key={i} className={`rounded-2xl overflow-hidden ${i === 0 ? "col-span-2 md:col-span-2 md:row-span-2" : ""}`}>
                <img src={g} alt={`TAF Villas Padel ${i + 1}`} loading="lazy" className="w-full h-full object-cover aspect-[4/3] hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Community CTA */}
      <section className="relative max-w-7xl mx-auto px-5 pb-24 z-10">
        <div className="relative rounded-3xl overflow-hidden shadow-xl">
          <img src={COMMUNITY} alt="Padel players" className="w-full h-[380px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1c3a29] via-[#1c3a29]/80 to-transparent" />
          <div className="palm-deco sway"
            style={{ width: 280, height: 280, top: -40, right: -40, "--rot": "-20deg", opacity: 0.55, backgroundImage: "url(/palm-frond.png)" }} />
          <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-14 max-w-xl">
            <h2 className="font-display font-black text-3xl sm:text-4xl mb-4 text-white">
              Ready to play padel in Kos?
            </h2>
            <p className="text-white/85 mb-7">
              Check live availability and book our court in seconds — powered by Playtomic.
            </p>
            <div>
              <Button
                onClick={openBooking}
                data-testid="cta-join-btn"
                className="rounded-full bg-white text-[#1c3a29] font-bold px-8 py-6 hover:-translate-y-1 transition-all"
              >
                {t("hero_cta")} <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* TAF family promo */}
      <section className="relative max-w-7xl mx-auto px-5 py-16 md:py-24 z-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="label-caps text-primary mb-3">{t("family_tag")}</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-foreground mb-4">
            {t("family_title")}
          </h2>
          <p className="text-muted-foreground leading-relaxed">{t("family_sub")}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Villas */}
          <a
            href="https://www.tafvillaskos.gr" target="_blank" rel="noopener noreferrer"
            data-testid="promo-villas"
            className="group relative rounded-3xl overflow-hidden shadow-lg ring-1 ring-black/5 block h-[420px]"
          >
            <img src="/villas-sunset.jpg" alt="TAF Beach Villas — private beachfront villas in Tigaki, Kos"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#17130F] via-[#17130F]/45 to-transparent" />
            <div className="absolute inset-0 p-8 flex flex-col justify-end">
              <div className="label-caps text-white/70 mb-2">Stay</div>
              <h3 className="font-display font-black text-2xl sm:text-3xl text-white mb-2">{t("villas_name")}</h3>
              <p className="text-white/85 text-sm leading-relaxed mb-5 max-w-md">{t("villas_desc")}</p>
              <span className="inline-flex items-center gap-2 text-white font-bold w-fit border-b-2 border-primary pb-0.5 group-hover:gap-3 transition-all">
                {t("villas_cta")} <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </a>

          {/* Tribe Beach */}
          <a
            href="https://www.tribebeachkos.gr" target="_blank" rel="noopener noreferrer"
            data-testid="promo-tribe"
            className="group relative rounded-3xl overflow-hidden shadow-lg ring-1 ring-black/5 block h-[420px]"
          >
            <img src="/tribe-sunset.jpg" alt="Tribe Beach Lounge — beach bar and lounge in Tigaki, Kos"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#17130F] via-[#17130F]/45 to-transparent" />
            <div className="absolute inset-0 p-8 flex flex-col justify-end">
              <div className="label-caps text-white/70 mb-2">Unwind</div>
              <h3 className="font-display font-black text-2xl sm:text-3xl text-white mb-2">{t("tribe_name")}</h3>
              <p className="text-white/85 text-sm leading-relaxed mb-5 max-w-md">{t("tribe_desc")}</p>
              <span className="inline-flex items-center gap-2 text-white font-bold w-fit border-b-2 border-primary pb-0.5 group-hover:gap-3 transition-all">
                {t("tribe_cta")} <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </a>
        </div>
      </section>

      {/* Our Court gallery */}
      <section className="relative max-w-7xl mx-auto px-5 pb-20 z-10" data-testid="court-gallery">
        <div className="label-caps text-primary mb-2">Our Court</div>
        <h2 className="font-display font-black text-3xl md:text-4xl text-foreground mb-3">
          A premium court in paradise
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-8">
          A pro-grade, glass-walled padel court set among the palms at TAF Beach Villas in Tigaki, Kos —
          floodlit for evening play and open to everyone, guests and visitors alike.
        </p>
        <div className="rounded-3xl overflow-hidden border border-border mb-4 group">
          <img
            src={COURT_BANNER}
            alt="TAF Villas Padel court in Tigaki, Kos"
            data-testid="court-photo-main"
            loading="lazy"
            className="w-full h-[320px] md:h-[460px] object-cover object-center group-hover:scale-[1.03] transition-transform duration-700"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {COURT_TILES.map((p, i) => (
            <div
              key={p.src}
              data-testid={`court-photo-${i}`}
              className="relative overflow-hidden rounded-2xl border border-border aspect-square group"
            >
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </section>

      {/* What players say */}
      <section className="relative max-w-7xl mx-auto px-5 pb-20 z-10" data-testid="reviews-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="label-caps text-primary mb-2">What players say</div>
            <h2 className="font-display font-black text-3xl md:text-4xl text-foreground">
              Loved by players in Kos
            </h2>
          </div>
          <Button
            onClick={openBooking}
            data-testid="leave-review-btn"
            variant="outline"
            className="rounded-full border-primary/30 bg-transparent text-foreground font-bold px-6 py-5 hover:bg-primary/5 transition-colors inline-flex items-center gap-2 self-start md:self-auto"
          >
            <Star className="w-4 h-4 text-primary" /> Leave a review on Playtomic
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TESTIMONIALS.map((rev, i) => (
            <div key={rev.name} data-testid={`review-card-${i}`}
              className="bg-card border border-border rounded-2xl p-5 flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="w-4 h-4 text-primary fill-primary" />
                ))}
              </div>
              <p className="text-sm text-foreground leading-relaxed flex-1">"{rev.quote}"</p>
              <div className="mt-4 text-sm font-bold text-foreground">
                {rev.name} <span className="text-muted-foreground font-medium">· {rev.country}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Playtomic community */}
      <section className="relative max-w-7xl mx-auto px-5 pb-20 z-10" data-testid="playtomic-community">
        <div className="bg-[#1c3a29] rounded-3xl p-8 md:p-14 text-white relative overflow-hidden">
          <div className="palm-deco hidden md:block"
            style={{ width: 320, height: 320, top: -60, right: -60, "--rot": "16deg", opacity: 0.18, backgroundImage: "url(/palm-frond.png)" }} />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-5">
              <img src="/playtomic-icon.png" alt="Playtomic" className="w-11 h-11 rounded-xl shadow-lg" />
              <span className="label-caps text-white/70">Part of the Playtomic community</span>
            </div>
            <h2 className="font-display font-black text-3xl md:text-5xl mb-4 max-w-2xl leading-tight">
              Join Playtomic and play padel your way
            </h2>
            <p className="text-white/85 max-w-2xl mb-9 text-[15px] md:text-base leading-relaxed">
              We book our court through <strong className="text-white">Playtomic</strong> — the world's largest padel
              community. Create a free account to see live availability, always get the best up-to-date prices,
              match with other players at your level, and reserve our Kos court anytime from your phone.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {[
                { icon: CalendarClock, t: "Live availability & latest prices" },
                { icon: Users, t: "Find players & open matches" },
                { icon: Trophy, t: "Join tournaments & Americanos" },
                { icon: Smartphone, t: "Book 24/7 from the app" },
              ].map((b, i) => (
                <div key={b.t} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`community-benefit-${i}`}>
                  <b.icon className="w-5 h-5 text-white/90 mt-0.5 shrink-0" />
                  <span className="text-sm font-semibold text-white/90">{b.t}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={openBooking}
                data-testid="community-book-btn"
                className="rounded-full bg-white text-[#1c3a29] font-bold px-7 py-6 hover:-translate-y-0.5 transition-transform inline-flex items-center gap-2"
              >
                Book on Playtomic <ExternalLink className="w-4 h-4" />
              </Button>
              <Button
                onClick={openBooking}
                data-testid="community-join-btn"
                variant="outline"
                className="rounded-full border-white/40 bg-transparent text-white font-bold px-7 py-6 hover:bg-white/10 transition-colors inline-flex items-center gap-2"
              >
                <Zap className="w-4 h-4" /> Create a Playtomic account
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SEO content */}
      <section className="relative max-w-4xl mx-auto px-5 pb-20 z-10">
        <div className="bg-card border rounded-2xl p-8">
          <h2 className="font-display font-bold text-2xl mb-4 text-foreground">
            Where to play padel on Kos Island
          </h2>
          <div className="text-muted-foreground leading-relaxed space-y-4 text-[15px]">
            <p>
              <strong className="text-foreground">TAF Villas Padel</strong> is the home of padel on
              <strong className="text-foreground"> Kos Island, Greece</strong>. Located in Tigaki as
              part of <strong className="text-foreground">TAF Beach Villas</strong>, we offer a single
              premium <strong className="text-foreground">padel court for rent</strong> — perfect for
              locals and visitors looking for where to play padel (paddle) in Kos.
            </p>
            <p>
              Our single premium padel court is booked through <strong className="text-foreground">Playtomic</strong>,
              where you'll always see <strong className="text-foreground">live availability and the latest prices</strong>.
              Rates vary by time of day, so Playtomic shows you the current price the moment you book — no outdated
              price lists, just the real rate for your slot. You can also rent a racket or balls at the
              TAF Beach Villas reception.
            </p>
            <p>
              Beyond court rental, the Playtomic community lets you <strong className="text-foreground">find other players</strong>
              at your level, join <strong className="text-foreground">open matches and tournaments</strong>, and book our
              Kos court anytime from your phone.
            </p>
            <p>
              Whether you live on Kos or are on holiday in Kos Town, Tigaki or Marmari, TAF Villas Padel
              is your spot to rent a padel court and play. Book online in seconds.
            </p>
          </div>
        </div>
      </section>

      {/* Find us */}
      <section className="relative max-w-7xl mx-auto px-5 pb-20 z-10" data-testid="find-us">
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          <div className="bg-card border border-border rounded-3xl p-8 flex flex-col justify-center">
            <div className="label-caps text-primary mb-2">{t("findus_tag")}</div>
            <h2 className="font-display font-black text-3xl md:text-4xl text-foreground mb-4">{t("findus_title")}</h2>
            <p className="flex items-start gap-2 text-foreground mb-3">
              <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" /> {t("findus_address")}
            </p>
            <p className="flex items-start gap-2 text-muted-foreground mb-6 text-sm">
              <IdCard className="w-5 h-5 text-primary mt-0.5 shrink-0" /> {t("findus_gear")}
            </p>
            <a href="https://www.google.com/maps/search/?api=1&query=TAF+Beach+Villas+Tigaki+Kos"
              target="_blank" rel="noreferrer" data-testid="directions-btn"
              className="inline-flex items-center gap-2 self-start rounded-full bg-primary text-primary-foreground font-bold px-6 py-3 hover:opacity-90 transition-opacity">
              <MapPin className="w-4 h-4" /> {t("findus_btn")}
            </a>
          </div>
          <div className="rounded-3xl overflow-hidden border border-border min-h-[300px]">
            <iframe title="TAF Villas Padel location" data-testid="find-us-map"
              src="https://www.google.com/maps?q=TAF+Beach+Villas+Tigaki+Kos&output=embed"
              className="w-full h-full min-h-[300px]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative max-w-4xl mx-auto px-5 pb-20 z-10">
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-3 text-foreground">
          {t("faq_title")}
        </h2>
        <div className="h-1 w-16 bg-primary rounded-full mb-10" />
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS[lang].map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="bg-card border rounded-2xl px-6 border-b"
            >
              <AccordionTrigger className="text-left font-display font-bold text-lg text-foreground hover:no-underline py-5">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed text-[15px] pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Footer */}
      <footer className="border-t py-10 relative z-10">
        <div className="max-w-7xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo />
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <a href="https://www.tafvillaskos.gr" target="_blank" rel="noopener noreferrer"
              data-testid="footer-villas-link" className="text-muted-foreground hover:text-primary font-semibold transition-colors">
              TAF Beach Villas
            </a>
            <a href="https://www.tribebeachkos.gr" target="_blank" rel="noopener noreferrer"
              data-testid="footer-tribe-link" className="text-muted-foreground hover:text-primary font-semibold transition-colors">
              Tribe Beach Lounge
            </a>
            <a href="mailto:info@tafvillaskos.gr" className="text-muted-foreground hover:text-primary font-semibold transition-colors">
              info@tafvillaskos.gr
            </a>
            <a href="https://wa.me/306936121177" target="_blank" rel="noopener noreferrer"
              data-testid="footer-whatsapp-link" className="text-muted-foreground hover:text-primary font-semibold transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
        <p className="max-w-7xl mx-auto px-5 text-muted-foreground text-sm mt-6 text-center md:text-left">
          © {new Date().getFullYear()} TAF Villas Padel · Tigaki, Kos, Greece · part of TAF Beach Villas
        </p>
      </footer>
    </div>
  );
}
